// Lo que el onboarding de marca recuerda entre pantallas y ante un refresh
// (ADR-2026-09-28-onboarding-v2-etapa-1). El escaneo y el lote siguen corriendo en el
// backend: con sus ids, la pantalla se reengancha en vez de empezar (y cobrar) de nuevo.
// Se guarda por empresa: otra cuenta en el mismo navegador no hereda nada.

import type { RespuestasDeLaEspera } from './marca';
import type { ObjetivoDePiezas, PiezaId, UsoDelLogo } from './piezas';
import type { OrigenDeLaMarca } from './tablero';

export interface EstadoDelOnboarding {
  companyId: string;
  /** Web (pegó un link) o manual ("Ahora no"). Sin él, se deduce del escaneo. */
  origen?: OrigenDeLaMarca;
  link?: string;
  escaneoId?: string;
  brandId?: string;
  respuestas?: RespuestasDeLaEspera;
  /** "Añadir nueva marca" (ADR-2026-10-02-marca-nueva-como-el-onboarding): las mismas pantallas, para una marca adicional. */
  flujo?: 'onboarding' | 'marca_nueva' | 'describir_marca';
  lote?: {
    ejecuciones: Partial<Record<PiezaId, string>>;
    uso: UsoDelLogo;
    producto: string;
    /** Qué eligió generar: con esto se rearman las etiquetas al reengancharse. */
    objetivo?: ObjetivoDePiezas;
    lanzadoEn: number;
  };
}

const CLAVE = 'atlas:onboarding_marca';

export function leerEstado(companyId: string | null): EstadoDelOnboarding | null {
  try {
    const raw = localStorage.getItem(CLAVE);
    const e = raw ? (JSON.parse(raw) as EstadoDelOnboarding) : null;
    if (!e || typeof e !== 'object') return null;
    if (companyId && e.companyId !== companyId) return null;
    return e;
  } catch {
    return null;
  }
}

export function guardarEstado(parcial: Partial<EstadoDelOnboarding> & { companyId: string }): EstadoDelOnboarding {
  const previo = leerEstado(parcial.companyId);
  const nuevo = { ...(previo ?? {}), ...parcial } as EstadoDelOnboarding;
  try {
    localStorage.setItem(CLAVE, JSON.stringify(nuevo));
  } catch {
    /* modo privado: se pierde el reenganche, nada más */
  }
  return nuevo;
}

/**
 * ¿El onboarding guardado es de OTRA cuenta que la de la sesión? (review Greptile #164,
 * ADR-2026-10-07-el-onboarding-guardado-se-valida-contra-la-sesion). Los pasos lo leen sin
 * empresa (`leerEstado(null)`) porque todavía no la saben: el marco del onboarding resuelve la
 * de la sesión al entrar y, si no coincide, lo tira. Sin estado guardado no hay nada que tirar.
 */
export function esDeOtraCuenta(estado: EstadoDelOnboarding | null, companyIdDeLaSesion: string | null): boolean {
  return Boolean(estado?.companyId) && estado!.companyId !== companyIdDeLaSesion;
}

/**
 * ¿Hay que tirar el onboarding guardado y arrancar de cero? Con la empresa de la sesión, si el
 * estado es de otra. Si esa consulta falló (`undefined`), por la marca guardada: si no existe
 * para esta sesión —RLS no deja ver la de otra cuenta— el estado no sirve. Si las dos fallan,
 * no se toca: es la red, y el progreso es probablemente de quien está
 * (review Greptile #164, ADR-2026-10-07-una-marca-que-no-existe-reinicia-el-onboarding).
 */
export function hayQueReiniciar(p: {
  guardado: EstadoDelOnboarding | null;
  companyIdDeLaSesion: string | null | undefined;
  marca?: 'ok' | 'no_existe' | 'error';
}): boolean {
  if (!p.guardado) return false;
  if (p.companyIdDeLaSesion !== undefined) return esDeOtraCuenta(p.guardado, p.companyIdDeLaSesion);
  return p.marca === 'no_existe';
}

export function limpiarEstado(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* noop */
  }
}
